import { useState } from 'react';
import { CircularProgress } from '@mui/material';
import './Footer.scss';
import useGenericSimulator from '../../hooks/useGenericSimulator';
import useUrlParams from '../../hooks/useUrlParams';
import { get_button_list } from '../../utils';
import { undeployScenario } from '../../service/GenericSimulatorApi';
import ConfirmationDialog from '../../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import CommandCenterCircularProgreeBar from '../../../command_center/custom_components/circular_progress/CircularProgressbar';
import { get_formated_time, showToastMessage } from '../../../../utils/commonFunctions';
import { useSimulationTimer } from '../../hooks/useSimulationTimer';

const Footer = () => {
    const { activeTab, setActiveTab, deploymentData } = useGenericSimulator();
    const { navigate_back, dashboard, mission_id, scenario_id } = useUrlParams();
    const isSummary = dashboard === 'summary';
    const ButtonsList = get_button_list(activeTab, dashboard);
    const [ending, setEnding] = useState(false);
    const [openDialogue, setOpenDialogue] = useState(false);
    const scenarioName = deploymentData?.data?.scenarioName || deploymentData?.data?.name || 'Scenario';

    const {
        currentSimulationTimeSec,
        scenarioStartTime,
        scenarioEndTime,
        scenarioStartTimeAbs,
        showScenarioTimerContainer
    } = useSimulationTimer();

    const handleEndSimulation = async () => {
        if (!mission_id || !scenario_id || ending) return;
        try {
            setEnding(true);
            setOpenDialogue(false);
            await undeployScenario(mission_id, scenario_id);
            navigate_back();
        } catch (error) {
            showToastMessage('error', 'Failed to end simulation.');
        } finally {
            setEnding(false);
        }
    };

    return (
        <div className='gs__footer'>
            {/* Left: Scenario info */}
            <div className='gs__footer__left'>
                <div className='gs__footer__scenario__info'>
                    <span className='gs__footer__scenario__name'>{scenarioName}</span>
                    <span className='gs__footer__scenario__mode'>{isSummary ? 'Summary' : 'Manual'}</span>
                </div>
            </div>
            {/* Middle: Scenario timer (progress ring + time) */}
            {showScenarioTimerContainer && (
                <div className='gs__scenario__timer__container'>
                    <CommandCenterCircularProgreeBar
                        simulation_current_time={scenarioStartTimeAbs + currentSimulationTimeSec}
                        simulation_start_time={scenarioStartTimeAbs + scenarioStartTime}
                        simulation_end_time={scenarioStartTimeAbs + scenarioEndTime}
                    />
                    <div className='gs__separator' />
                    <div className='gs__time__container'>
                        <span>{get_formated_time(scenarioStartTimeAbs + currentSimulationTimeSec)}</span>
                        <span>{get_formated_time(scenarioStartTimeAbs + scenarioEndTime)}</span>
                    </div>
                </div>
            )}
            {/* Center: Tab buttons */}
            <div className='gs__footer__center'>
                {ButtonsList.map((button) => {
                    const { label, svg, show, active } = button;
                    return show ? (
                        <button
                            key={label}
                            className={`gs__footer__tab ${active ? 'active' : ''}`}
                            onClick={() => setActiveTab(label.toLowerCase() as 'hud' | 'monitor')}
                        >
                            {svg}
                            <span>{label}</span>
                        </button>
                    ) : null;
                })}
            </div>

            {/* Right: Actions */}
            <div className='gs__footer__right'>
                {isSummary ? (
                    <button className='gs__footer__action__btn gs__footer__end__btn' onClick={navigate_back}>
                        Close
                    </button>
                ) : (
                    <button
                        className='gs__footer__action__btn gs__footer__end__btn'
                        onClick={() => setOpenDialogue(true)}
                        disabled={ending}
                    >
                        {ending ? 'Retiring' : 'End Simulation'}
                        {ending && <CircularProgress size={16} color="inherit" />}
                    </button>
                )}
            </div>

            {/* Confirmation dialog — same component used in Command Center */}
            <ConfirmationDialog
                type='cancel'
                open={openDialogue}
                title='End Scenario Simulation?'
                description='Ending the simulation will undeploy this scenario. This action cannot be undone.'
                loading={ending}
                onCancel={() => setOpenDialogue(false)}
                onConfirm={handleEndSimulation}
                confirm_button_text='End Simulation'
            />
        </div>
    );
};

export default Footer;
